import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CalendarEvent } from '@daily/shared';
import { api } from '@/lib/api';
import { memberColours, colour } from '@/lib/theme';
import { format, isSameDay } from 'date-fns';
import { X, CalendarDays } from 'lucide-react';

interface TodayDetailProps {
  onClose: () => void;
}

function WhoChip({ who }: { who: string[] }) {
  const who0 = who[0];
  const label =
    who.length === 1 && who0
      ? who0.charAt(0).toUpperCase() + who0.slice(1)
      : who.length > 1
        ? `+${who.length}`
        : '·';
  const memberKey = who0 ? memberColours[who0] : undefined;
  const c = memberKey ? colour[memberKey] : colour.mustard;
  return (
    <span
      className="text-[10px] font-bold tracking-[0.06em] uppercase px-2 py-[3px] rounded-full flex-shrink-0"
      style={{ background: `${c}1f`, color: c }}
    >
      {label}
    </span>
  );
}

function isAllDay(e: CalendarEvent): boolean {
  // Google all-day events come through as midnight with no time component.
  const d = new Date(e.startsAt);
  return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && !e.endsAt;
}

export function TodayDetail({ onClose }: TodayDetailProps) {
  const today = new Date();
  const fromDate = today.toISOString().slice(0, 10);
  const toDate = new Date(today.getTime() + 14 * 86_400_000).toISOString().slice(0, 10);

  const events = useQuery<CalendarEvent[]>({
    queryKey: ['events', fromDate, toDate],
    queryFn: () => api.getEvents(fromDate, toDate),
    staleTime: 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Group events by calendar day.
  const sorted = [...(events.data ?? [])].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const groups: { date: Date; items: CalendarEvent[] }[] = [];
  for (const ev of sorted) {
    const d = new Date(ev.startsAt);
    const last = groups[groups.length - 1];
    if (last && isSameDay(last.date, d)) {
      last.items.push(ev);
    } else {
      groups.push({ date: d, items: [ev] });
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-lg shadow-lg overflow-hidden max-h-[90vh] overflow-y-auto bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card/95 backdrop-blur px-5 py-4 border-b border-black/[0.06] flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <CalendarDays size={16} className="text-tomato" />
            <h2 className="font-serif italic text-display-sm text-ink">The diary</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-black/5"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          {events.isLoading ? (
            <p className="text-body-sm text-inkFaded">Reading the calendar…</p>
          ) : groups.length === 0 ? (
            <p className="font-serif italic text-inkFaded text-body-lg">
              A clear fortnight. Nothing in the diary.
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {groups.map(({ date, items }) => {
                const isToday = isSameDay(date, today);
                return (
                  <div key={date.toISOString()}>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-eyebrow uppercase tracking-[0.16em] text-inkFaded">
                        {isToday ? 'Today' : format(date, 'EEEE')}
                      </span>
                      <span className="text-[11px] text-inkGhost">{format(date, 'd MMM')}</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      {items.map((e) => {
                        const who0 = e.who[0];
                        const memberKey = who0 ? memberColours[who0] : undefined;
                        const bar = memberKey ? colour[memberKey] : colour.mustard;
                        return (
                          <div
                            key={e.id}
                            className="grid grid-cols-[52px_3px_1fr_auto] gap-2.5 items-center"
                          >
                            <span className="font-mono text-[11px] text-inkFaded">
                              {isAllDay(e) ? 'all day' : format(new Date(e.startsAt), 'HH:mm')}
                            </span>
                            <span
                              className="w-[3px] h-7 rounded-sm self-stretch"
                              style={{ background: bar }}
                            />
                            <div className="min-w-0">
                              <div className="font-serif text-[15px] leading-snug truncate">
                                {e.title}
                              </div>
                              {e.location && (
                                <div className="text-[11px] text-inkFaded truncate">
                                  {e.location}
                                </div>
                              )}
                            </div>
                            <WhoChip who={e.who} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
